import { useMemo } from 'react';

import ViewModeConstants, { ViewMode } from '../../../constants/ViewModeConstants';
import { SURVEY_LAYERS } from '../../../constants/SurveyLayers';
import { isSurveyLayer } from '../../Prescription/PrescriptionJob';
import {
  getDemandScales,
  getStandardDemandScales,
  renderStandardDemandScale,
} from '../../DemandScale/DemandScale';
import { getScaleItem } from '../LayerStatisticsUtils';
import { getColoringStrategy } from '../../../model/surveys/SurveyColoringStrategies';
import { useLangFile } from '../../../context/LanguageContext';
import { useSurveyCountryCode } from '../../../hooks/useSurveyCountryCode';
import { useSurveyLayerViewCapabilities } from '../../../context/SurveyContext';
import type {
  ColorScaleEntry,
  FieldRef,
  LayerStatisticsBucket,
  LayerStatisticsGrid,
  LayerStatisticsSource,
  SurveyClassificationValues,
  SurveyReferenceValues,
} from './LayerStatisticsSourceTypes';

type SurveyStatisticsSourceParams = {
  layer: string | null;
  viewMode: ViewMode;
  classificationsEnabled: boolean;
  variationsEnabled: boolean;
  selectedSurveyReferenceValues: SurveyReferenceValues | null;
  selectedSurveyClassificationValues: SurveyClassificationValues;
  selectedField: FieldRef | null;
};

const emptySource: LayerStatisticsSource = {
  values: null,
  buckets: null,
  summaryData: null,
  classificationData: null,
  useClassificationBuckets: false,
  isReady: false,
  selectedField: null,
};

const createScaleStatisticsSource = (
  values: LayerStatisticsGrid,
  surveyScale: ColorScaleEntry[],
  selectedField: FieldRef | null
): LayerStatisticsSource => {
  const buckets = surveyScale.map(
    (scale: ColorScaleEntry, idx: number, arr: ColorScaleEntry[]) => ({
      item: getScaleItem(scale, idx, arr, true, 2, null),
      min: Number(scale.min),
      max: Number(scale.max),
      count: 0,
    })
  );

  return {
    values,
    buckets,
    summaryData: null,
    classificationData: null,
    useClassificationBuckets: false,
    isReady: true,
    selectedField,
  };
};

const createCustomClassificationStatisticsSource = (
  referenceValues: LayerStatisticsGrid,
  layer: string,
  classificationsEnabled: boolean,
  selectedSurveyClassificationValues: SurveyClassificationValues,
  LangFile,
  selectedField: FieldRef | null
): LayerStatisticsSource => {
  const selectedLayerClassificationValues = selectedSurveyClassificationValues[layer];
  if (!selectedLayerClassificationValues || !selectedLayerClassificationValues['mappings']) {
    return emptySource;
  }

  const classificationMappings = selectedLayerClassificationValues['mappings']['FINE'];
  const classificationData = selectedLayerClassificationValues['values'];
  if (!classificationMappings || !classificationData) {
    return emptySource;
  }

  return {
    values: classificationData,
    buckets: getDemandScales(classificationsEnabled, classificationMappings, LangFile),
    summaryData: referenceValues,
    classificationData,
    useClassificationBuckets: true,
    isReady: true,
    selectedField: selectedField,
  };
};

const createStandardClassificationStatisticsSource = (
  values: LayerStatisticsGrid,
  layer: string,
  viewMode: string,
  classificationsEnabled: boolean,
  surveyCountryCode: string | null,
  selectedSurveyReferenceValues: SurveyReferenceValues | null,
  LangFile,
  selectedField: FieldRef | null
): LayerStatisticsSource => {
  const buckets: LayerStatisticsBucket[] = [];
  (
    getStandardDemandScales(
      classificationsEnabled,
      layer,
      viewMode !== ViewModeConstants.OVERVIEW
    ) || []
  ).forEach((scale: { min: number; max: number } | null) => {
    if (!scale) {
      return;
    }

    buckets.push({
      item: renderStandardDemandScale(scale, LangFile),
      min: scale.min,
      max: scale.max,
      count: 0,
    });
  });

  const isDkRtDiff = layer === SURVEY_LAYERS.RT && surveyCountryCode === 'DK';
  const valuesForBuckets = isDkRtDiff
    ? selectedSurveyReferenceValues && selectedSurveyReferenceValues[SURVEY_LAYERS.RT_DIFF]
    : values;

  if (isDkRtDiff && !valuesForBuckets) {
    return emptySource;
  }

  return {
    values: valuesForBuckets,
    buckets,
    summaryData: isDkRtDiff ? values : null,
    classificationData: null,
    useClassificationBuckets: false,
    isReady: true,
    selectedField: selectedField,
  };
};

const useSurveyStatisticsSource = ({
  layer,
  viewMode,
  classificationsEnabled,
  variationsEnabled,
  selectedSurveyReferenceValues,
  selectedSurveyClassificationValues,
  selectedField,
}: SurveyStatisticsSourceParams): LayerStatisticsSource => {
  const LangFile = useLangFile();
  const surveyCountryCode = useSurveyCountryCode();
  const surveyLayerViewCapabilities = useSurveyLayerViewCapabilities(layer, viewMode);

  return useMemo<LayerStatisticsSource>(() => {
    // Read straight from the store so the reference and classification grids always
    // belong to the same field; the store clears both together on field change.
    const values = (layer && selectedSurveyReferenceValues?.[layer]) || null;
    if (!isSurveyLayer(layer) || !values) {
      return emptySource;
    }

    const coloringStrategy = getColoringStrategy(
      layer,
      viewMode,
      classificationsEnabled,
      variationsEnabled,
      values
    );
    const surveyScale = coloringStrategy.scale || null;

    if (surveyScale) {
      return createScaleStatisticsSource(values, surveyScale, selectedField);
    }

    if (!classificationsEnabled) {
      return emptySource;
    }

    if (
      surveyLayerViewCapabilities.enableCustomClassification &&
      Object.keys(selectedSurveyClassificationValues).length > 0
    ) {
      return createCustomClassificationStatisticsSource(
        values,
        layer,
        classificationsEnabled,
        selectedSurveyClassificationValues,
        LangFile,
        selectedField
      );
    }

    return createStandardClassificationStatisticsSource(
      values,
      layer,
      viewMode,
      classificationsEnabled,
      surveyCountryCode,
      selectedSurveyReferenceValues,
      LangFile,
      selectedField
    );
  }, [
    LangFile,
    classificationsEnabled,
    layer,
    selectedSurveyClassificationValues,
    selectedSurveyReferenceValues,
    surveyCountryCode,
    surveyLayerViewCapabilities,
    viewMode,
    selectedField,
  ]);
};

export default useSurveyStatisticsSource;
